import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { mergeStable } from "@/lib/mergeStable";
import { onPanicChanged, subscribePendingPanic, forcePendingPanicRefresh } from "@/lib/panicEvents";
import { AlertOctagon, MapPin, Phone } from "lucide-react";

/** Consume el poller ÚNICO y compartido de /panic?status=pendiente
 * (ver lib/panicEvents.js) — ya no dispara su propio setInterval contra el
 * mismo endpoint que Panic.jsx usa en la pestaña "Pendientes", evitando el
 * doble polling. Mientras haya al menos una alerta pendiente, el sonido se
 * repite (no un solo beep) y se muestra el detalle completo (objetivo,
 * vigilador, hora, ubicación, botón Llamar) para que no haya que adivinar
 * de qué se trata.
 *
 * Optimizaciones de performance (ver comentarios inline):
 * - UN solo AudioContext reutilizable + los osciladores agendados con
 *   setTimeout se rastrean en un ref y se cancelan al desmontar o al quedar
 *   sin alertas.
 * - setAlerts sólo se dispara si los datos realmente cambiaron (mergeStable).
 * - Escucha el evento "panic:changed" (disparado por la página /alertas al
 *   atender/resolver) para pedir un refresh inmediato del poller compartido,
 *   sin esperar el próximo ciclo. */
export function PanicBanner() {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const lastCountRef = useRef(0);
  const ctxRef = useRef(null);
  const toneTimeoutsRef = useRef([]);

  const stopAllTones = useCallback(() => {
    toneTimeoutsRef.current.forEach(clearTimeout);
    toneTimeoutsRef.current = [];
  }, []);

  const playBeep = useCallback((urgent) => {
    const ctx = ctxRef.current;
    if (!ctx) return; // el usuario todavía no interactuó — se omite en silencio
    try {
      const tones = urgent ? [880, 660, 880, 660, 880] : [880, 660, 880];
      tones.forEach((freq, i) => {
        const scheduleId = setTimeout(() => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "square"; osc.frequency.value = freq;
          gain.gain.value = 0.15;
          osc.connect(gain); gain.connect(ctx.destination);
          osc.start();
          const stopId = setTimeout(() => osc.stop(), 200);
          toneTimeoutsRef.current.push(stopId);
        }, i * 250);
        toneTimeoutsRef.current.push(scheduleId);
      });
    } catch {}
  }, []);

  useEffect(() => {
    const onFirstInteract = () => {
      try {
        ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      } catch {}
      window.removeEventListener("click", onFirstInteract);
      window.removeEventListener("keydown", onFirstInteract);
    };
    window.addEventListener("click", onFirstInteract, { once: true });
    window.addEventListener("keydown", onFirstInteract, { once: true });

    const handleRows = (rows) => {
      if (rows.length === 0) stopAllTones(); // corta cualquier tono agendado apenas no quedan pendientes
      else playBeep(rows.length > lastCountRef.current);
      lastCountRef.current = rows.length;
      setAlerts((prev) => {
        const { merged, changed } = mergeStable(prev, rows);
        return changed ? merged : prev;
      });
    };
    const unsubscribe = subscribePendingPanic(handleRows);
    const offPanicChanged = onPanicChanged(forcePendingPanicRefresh); // pide un refresh inmediato al atender/resolver desde /alertas

    return () => {
      unsubscribe();
      stopAllTones();
      offPanicChanged();
      window.removeEventListener("click", onFirstInteract);
      window.removeEventListener("keydown", onFirstInteract);
    };
  }, [playBeep, stopAllTones]);

  if (alerts.length === 0) return null;

  const alertsPath = user?.role === "admin" ? "/admin/alertas" : user?.role === "supervisor" ? "/supervisor/alertas" : null;
  const top = alerts[0];
  const mapsUrl = top.latitude ? `https://www.google.com/maps?q=${top.latitude},${top.longitude}` : null;

  return (
    <div data-testid="panic-banner" style={{
      position: "sticky", top: 0, zIndex: 80,
      background: "#BF3437", color: "white",
      padding: "12px 18px",
      animation: "pulse-red 1.6s infinite",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <AlertOctagon size={22} />
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: "0.03em" }} data-testid="panic-banner-count">
            {top.is_tenant_sos ? "SOS DE VECINO" : (alerts.length === 1 ? "SOS ACTIVO" : `${alerts.length} SOS ACTIVOS`)} — {top.client_name || "Sin objetivo asignado"}{top.tenant_dwelling ? ` · ${top.tenant_dwelling}` : ""}
          </div>
          <div style={{ fontSize: 11, opacity: 0.9 }}>
            {top.user_name} ({top.user_role}) · {new Date(top.created_at).toLocaleTimeString("es-AR")}
            {top.routed_supervisor_name ? ` · Responsable: ${top.routed_supervisor_name}` : ""}
          </div>
        </div>
        {mapsUrl && (
          <a href={mapsUrl} target="_blank" rel="noreferrer" data-testid="panic-banner-map"
             style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "white", fontSize: 11, fontWeight: 700, textDecoration: "underline" }}>
            <MapPin size={13} /> Ver ubicación
          </a>
        )}
        {(top.user_phone || top.sos_contact_phone) && (
          <a href={`tel:${(top.user_phone || top.sos_contact_phone).replace(/[^\d+]/g, "")}`} data-testid="panic-banner-call"
             style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "rgba(255,255,255,0.18)", padding: "6px 12px", borderRadius: 2, color: "white", fontSize: 11, fontWeight: 800, textDecoration: "none", textTransform: "uppercase" }}>
            <Phone size={12} /> Llamar
          </a>
        )}
        {alertsPath && (
          <Link to={alertsPath} data-testid="panic-banner-link"
                style={{ background: "#0A0A0A", color: "white", padding: "6px 12px", borderRadius: 2, fontSize: 11, fontWeight: 800, textTransform: "uppercase", textDecoration: "none" }}>
            Atender ahora →
          </Link>
        )}
      </div>
    </div>
  );
}
